import { Component, OnInit, ViewChild, ElementRef, Input, ChangeDetectionStrategy, ViewEncapsulation, OnDestroy } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Observable, Subject } from 'rxjs';
import { BdGraph } from 'src/lib/graph/graph.model';
import { ViewTreeNode } from 'src/app/engine/models/view.tree-node';
import { TreeTheme } from 'src/app/shared/components/tree/tree/tree.component';
import { ViewService } from '../../view.service';
import { TreeSearchDisplayMode } from 'src/app/shared/components/tree/tree-search/tree-search.component';
import { ViewState } from '../../state/view.state';
import { ViewActions } from '../../state/view.actions';
import { SetGraph } from 'src/app/engine/state/engine.actions';
import { EngineService } from 'src/app/engine/engine.service';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-view-search',
  templateUrl: './view-search.component.html',
  styleUrls: ['./view-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ViewSearchComponent implements OnInit, OnDestroy {
  // ---------- View Children ----------
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  


  // ---------- Observables ----------
  @Select(EngineState.getViewGraph)
  viewGraph$: Observable<BdGraph<ViewTreeNode>>;

  @Select(ViewState.getSearchDisplayMode)
  searchDisplayMode$: Observable<TreeSearchDisplayMode>;

  ngUnsubscribe = new Subject();


  // ---------- Class Variables ----------
  query = '';
  treeTheme = TreeTheme;
  treeSearchDisplayMode = TreeSearchDisplayMode;

  constructor(
    private viewService: ViewService,
    private engineService: EngineService,
    private store: Store
    ) { }

  ngOnInit() {
    // set view graph on chapter change
    this.initViewGraphSubscription();

    // Always focus on search box when list / tree view changes
    this.initDisplayModeSubscription();
  }

  private initViewGraphSubscription() {
    this.store
      .select(StoryChaptersState.getSelectedChapterId)
      .pipe(
        filter((id) => id != null),
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.store.dispatch(
          new SetGraph(this.engineService.createViewGraph({}, false))
        );
      });
  }

  private initDisplayModeSubscription() {
    this.searchDisplayMode$.pipe(
      takeUntil(this.ngUnsubscribe),
    ).subscribe((mode) => {
      // Inside timeout so that element has time to render
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      });
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ---------- Interaction ----------

  handleSearchKeyup(event: KeyboardEvent, value: string) {
    if (event.key === 'Escape') {
      this.handleExitClick();
      this.searchInput.nativeElement.blur();
    }
    this.query = value;
  }

  handleExitClick() {
    this.query = '';
    this.viewService.toggleSearch(null, false);
  }

  toggleDisplayMode() {
    let displayMode = this.store.selectSnapshot(ViewState.getSearchDisplayMode);
    displayMode = (displayMode === TreeSearchDisplayMode.List) ? TreeSearchDisplayMode.Tree : TreeSearchDisplayMode.List;
    this.setDisplayMode(displayMode);
  }

  setDisplayMode(displayMode: TreeSearchDisplayMode) {
    this.store.dispatch(new ViewActions.SetSearchDisplayMode(displayMode));
  }

  handleTreeButtonClick() {
    this.setDisplayMode(TreeSearchDisplayMode.Tree);
  }

  handleListButtonClick() {
    this.setDisplayMode(TreeSearchDisplayMode.List);
  }
}
